/**
 * Date and duration handling for the EMS API.
 *
 * The API is inconsistent about timezone offsets by design (see
 * docs/api/mail-processing-contract.md §1.3): request-stamped values such as
 * `asOf` carry an offset, while every value read from the database does not.
 * Those offset-less values are server-local time, which is Africa/Johannesburg.
 *
 * SAST is UTC+02:00 all year with no daylight saving, so a fixed offset is
 * correct here and no timezone database is needed.
 *
 * Durations arrive as .NET TimeSpan strings (contract §1.4), not ISO 8601 and
 * not seconds, so they need their own parser.
 *
 * This module also owns the two formatting directions those rules imply:
 * `formatShortDuration` renders a duration for display, and
 * `toEmsApiTimestamp` produces the server-local wall-clock values the history
 * endpoint's query parameters expect.
 */

const SAST_OFFSET = "+02:00";
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Parses an EMS timestamp. An offset-less value is read as Africa/Johannesburg
 * rather than as the browser's local time.
 *
 * Returns undefined for absent or unparseable input, never an Invalid Date —
 * an Invalid Date propagates silently into arithmetic and renders as "NaN".
 */
export function parseEmsDate(value: string | undefined): Date | undefined {
    if (!value) {
        return undefined;
    }

    const parsed = new Date(HAS_OFFSET.test(value) ? value : value + SAST_OFFSET);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

const TIMESPAN = /^(-)?(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?$/;

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

/**
 * Parses a .NET TimeSpan — `[-][d.]hh:mm:ss[.fffffff]` — into seconds.
 *
 * This is not ISO 8601 and not a number of seconds on the wire; see contract
 * §1.4. Deliberately strict: an unrecognised shape returns undefined rather
 * than a plausible wrong number. Absent input returns undefined silently; a
 * value that is present but unparseable also warns, because that means the
 * wire format changed and it would otherwise be indistinguishable from "not
 * measured".
 */
export function parseEmsTimeSpan(value: string | undefined): number | undefined {
    if (!value) {
        return undefined;
    }

    const match = TIMESPAN.exec(value);
    if (!match) {
        // Present but unparseable. Distinct from absent, and worth a noise:
        // the API only sends the .NET "c" format, so this means the wire
        // format changed. Returning undefined silently would be
        // indistinguishable from "not measured" and would hide that.
        console.warn(`parseEmsTimeSpan: unrecognised TimeSpan "${value}" — has the API format changed?`);
        return undefined;
    }

    const [, sign, days, hours, minutes, seconds, fraction] = match;

    let total =
        (days ? parseInt(days, 10) : 0) * SECONDS_PER_DAY +
        parseInt(hours, 10) * SECONDS_PER_HOUR +
        parseInt(minutes, 10) * SECONDS_PER_MINUTE +
        parseInt(seconds, 10);

    if (fraction) {
        total += parseFloat("0." + fraction);
    }

    return sign ? -total : total;
}

/**
 * Formats a duration in seconds coarsely, for table cells and chips: "<1m",
 * "52m", "4h", "1d".
 *
 * Truncates rather than rounds, so a duration never reads as longer than it
 * actually is. Negative input — possible from clock skew between collector
 * nodes — collapses to "<1m" rather than displaying a negative age, which
 * would look like a data fault to an operator when it is not one.
 */
export function formatShortDuration(seconds: number): string {
    if (seconds < 0) {
        // Clock skew between collector nodes can produce a small negative age.
        // Guarded explicitly rather than relying on the sub-minute branch
        // below, so reordering the branches cannot silently change this.
        return "<1m";
    }
    if (seconds < SECONDS_PER_MINUTE) {
        return "<1m";
    }
    if (seconds < SECONDS_PER_HOUR) {
        return `${Math.floor(seconds / SECONDS_PER_MINUTE)}m`;
    }
    if (seconds < SECONDS_PER_DAY) {
        return `${Math.floor(seconds / SECONDS_PER_HOUR)}h`;
    }
    return `${Math.floor(seconds / SECONDS_PER_DAY)}d`;
}

// The same fixed offset as SAST_OFFSET above, in milliseconds for arithmetic.
// If the offset ever changes, both must change.
const SAST_OFFSET_MS = 2 * SECONDS_PER_HOUR * 1000;

/**
 * Formats an instant as a server-local wall-clock timestamp with no offset
 * marker, for the `from` and `to` query parameters on the history endpoint.
 *
 * The server compares these against stored values that are themselves
 * server-local wall-clock with no offset, so sending UTC would shift the
 * requested window by two hours. Shifting the instant and then reading its
 * UTC parts yields the SAST reading without needing a timezone database.
 *
 * Throws if `value` is an Invalid Date. Unlike `parseEmsDate`, which parses
 * untrusted wire text and returns undefined, this takes an in-process Date —
 * an invalid one is a caller bug, and throwing surfaces it immediately rather
 * than sending a garbage window to the server.
 */
export function toEmsApiTimestamp(value: Date): string {
    // slice(0, 19) keeps YYYY-MM-DDTHH:mm:ss and drops the fraction and Z.
    // Assumes a 4-digit year; toISOString expands years outside 0000-9999.
    return new Date(value.getTime() + SAST_OFFSET_MS).toISOString().slice(0, 19);
}
